import { Tabs } from "@design";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCapabilities } from "@/features/account/useCurrentUser";

export type KnowledgeSection = "wiki" | "files";

/** A space's Knowledge area: its wiki (the knowledge graph) and its uploaded files, one URL each. */
export function knowledgePath(spaceUuid: string, section: KnowledgeSection): string {
  return section === "wiki" ? `/spaces/${spaceUuid}/knowledge` : `/spaces/${spaceUuid}/knowledge/files`;
}

/** One wiki page, opened in its space's Knowledge area. */
export function wikiPagePath(spaceUuid: string, path: string): string {
  return `${knowledgePath(spaceUuid, "wiki")}?file=${encodeURIComponent(path)}`;
}

/** One of the space's files, opened in its Files page. */
export function knowledgeFilePath(spaceUuid: string, fileUuid: string): string {
  return `${knowledgePath(spaceUuid, "files")}?file=${encodeURIComponent(fileUuid)}`;
}

export function KnowledgeTabs({ spaceUuid, active }: Readonly<{ spaceUuid: string; active: KnowledgeSection }>) {
  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  // Without the knowledge graph the area is just Files: no tabs.
  if (!useCapabilities().knowledgeGraphEnabled) return null;
  return (
    <Tabs
      aria-label={t("tabs.label")}
      tabs={[
        { value: "wiki", label: t("tabs.wiki") },
        { value: "files", label: t("tabs.files") },
      ]}
      value={active}
      onChange={(next) => navigate(knowledgePath(spaceUuid, next as KnowledgeSection))}
    />
  );
}
